/**
 * Client detail access helpers
 *
 * Single source of truth for "can this user view this client's detail page?"
 * Mirrors the backend rule in ClientsController.checkClientAccess and is used
 * by every frontend surface that links to /clients/:id so that links are
 * suppressed for clients the caller cannot open. Always pair the link
 * suppression with the route guard on the detail page itself — we treat the
 * frontend gate as UX, not security; the API is still the authority.
 *
 * Rule:
 *   - clients:detail:any       → allow (admin / sales-manager bypass)
 *   - client.salesRepId == me OR client.alternateSalesRepId == me
 *     AND clients:view:self    → allow (assigned salesperson)
 *   - client.salesRepId == null AND clients:view:unassigned → allow
 *     (unassigned client; salesperson who can claim leads)
 *   - else → deny
 */

import { PermissionService } from '../services/permission.service';

export interface ClientAccessSubject {
	salesRepId?: number | null;
	alternateSalesRepId?: number | null;
}

export function canViewClientDetail(
	client: ClientAccessSubject | null | undefined,
	currentUserId: number | null | undefined,
): boolean {
	if (PermissionService.can('clients:detail:any')) {
		return true;
	}

	if (client == null) {
		return false;
	}

	const isUnassigned = client.salesRepId == null;

	if (isUnassigned) {
		return PermissionService.can('clients:view:unassigned');
	}

	if (!PermissionService.can('clients:view:self')) {
		return false;
	}

	if (currentUserId == null) {
		return false;
	}

	return (
		client.salesRepId === currentUserId ||
		client.alternateSalesRepId === currentUserId
	);
}
